import { useState, useEffect } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { profileService } from '@/services/profileService'
import { attestationService } from '@/services/attestationService'
import { parseFlexibleInput } from '@/utils'
import { ProfilePreferencesScreenDesktop } from './ProfilePreferencesScreenDesktop'
import { ProfilePreferencesScreenMobile } from './ProfilePreferencesScreenMobile'
import { 
  getDefaultProfilePreferencesFormData, 
  INDUSTRIES_OF_INTEREST,
  type ProfilePreferencesScreenBaseProps 
} from './ProfilePreferencesScreenBase'

/**
 * ProfilePreferencesScreen component
 * Renders mobile or desktop version based on screen size
 * 
 * Uses component split pattern for different layouts:
 * - Mobile: Vertical layout with compact spacing
 * - Desktop: Absolute positioning layout matching Figma design
 * 
 * Manages form state, validation, and submission logic
 */
const ProfilePreferencesScreen = () => {
  const isMobile = useIsMobile()
  const [formData, setFormData] = useState(getDefaultProfilePreferencesFormData())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Fetch attestation data to prefill country
  useEffect(() => {
    const fetchAttestationData = async () => {
      try {
        const attestations = await attestationService.getUserAttestations()
        if (attestations && attestations.length > 0) {
          // Get the most recent attestation
          const latestAttestation = attestations.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )[0]
          
          if (latestAttestation.country) {
            setFormData(prev => ({
              ...prev,
              country: latestAttestation.country
            }))
          }
        }
      } catch (error) {
        // Silently fail - country will just be empty if attestation fetch fails
        console.error('Failed to fetch attestation data:', error)
      }
    }

    fetchAttestationData()
  }, [])

  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleAssetAdd = (type: 'buy' | 'sell', asset: string) => {
    const parsedAssets = parseFlexibleInput(asset)
    if (parsedAssets.length === 0) return

    const field = type === 'buy' ? 'companies_of_interest_buy' : 'companies_of_interest_sell'
    const currentAssets = formData[field]
    
    // Filter out duplicates
    const newAssets = parsedAssets.filter(
      a => !currentAssets.some(existing => existing.toLowerCase() === a.toLowerCase())
    )

    if (newAssets.length > 0) {
      handleFormDataChange({ [field]: [...currentAssets, ...newAssets] })
    }
  }

  const handleAssetRemove = (type: 'buy' | 'sell', asset: string) => {
    const field = type === 'buy' ? 'companies_of_interest_buy' : 'companies_of_interest_sell'
    handleFormDataChange({ 
      [field]: formData[field].filter(a => a !== asset) 
    })
  }

  const handleIndustryToggle = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industries_of_interest: prev.industries_of_interest.includes(industry)
        ? prev.industries_of_interest.filter(i => i !== industry)
        : [...prev.industries_of_interest, industry]
    }))
  }

  const handleSelectAllIndustries = () => {
    setFormData(prev => ({
      ...prev,
      industries_of_interest: [...INDUSTRIES_OF_INTEREST]
    }))
  }

  const handleClearAllIndustries = () => {
    setFormData(prev => ({
      ...prev,
      industries_of_interest: []
    }))
  }

  const validateForm = () => {
    // Validate required profile fields
    if (!formData.first_name.trim()) {
      setError('First name is required')
      return false
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required')
      return false
    }
    if (!formData.job_title.trim()) {
      setError('Job title is required')
      return false
    }
    if (!formData.company_name.trim()) {
      setError('Company name is required')
      return false
    }
    if (!formData.country.trim()) {
      setError('Country is required')
      return false
    }

    // Validate investment amounts
    const { preferred_investment_amount_min, preferred_investment_amount_max } = formData
    
    // If both are selected, validate min <= max
    if (preferred_investment_amount_min && preferred_investment_amount_max) {
      if (preferred_investment_amount_min > preferred_investment_amount_max) {
        setError('Maximum investment amount must be greater than or equal to minimum amount')
        return false
      }
    }
    
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validate form
    if (!validateForm()) {
      setLoading(false)
      return
    }

    try {
      // Prepare data for API
      const profileData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        job_title: formData.job_title,
        company_name: formData.company_name,
        country: formData.country,
        companies_of_interest_buy: formData.companies_of_interest_buy,
        companies_of_interest_sell: formData.companies_of_interest_sell,
        industries_of_interest: formData.industries_of_interest,
        preferred_investment_amount_min: formData.preferred_investment_amount_min,
        preferred_investment_amount_max: formData.preferred_investment_amount_max
      }

      await profileService.updateProfile(profileData)
      
      // Redirect to main screen on success
      window.location.href = '/'
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSkip = () => {
    // Skip this step and go to main screen
    window.location.href = '/'
  }

  const baseProps: ProfilePreferencesScreenBaseProps = {
    formData,
    loading,
    error,
    onFormDataChange: handleFormDataChange,
    onAssetAdd: handleAssetAdd,
    onAssetRemove: handleAssetRemove,
    onIndustryToggle: handleIndustryToggle,
    onSelectAllIndustries: handleSelectAllIndustries,
    onClearAllIndustries: handleClearAllIndustries,
    onSubmit: handleSubmit,
    onSkip: handleSkip,
  }

  // Component Split pattern - completely separate mobile and desktop rendering
  if (isMobile) {
    return <ProfilePreferencesScreenMobile {...baseProps} />
  }

  return <ProfilePreferencesScreenDesktop {...baseProps} />
}

export default ProfilePreferencesScreen

